import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { Province } from '../../types/province';
import { getRaceDisplayName, getPersonalityDisplayName, getRaceUnitName } from '../../constants/game';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { deriveAlerts } from '../../utils/alerts';
import { isEffectBeneficialToOwner } from '../../utils/effectOwnerImpact';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { COLORS } from '../../constants/theme';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { EffectRow } from '../shell/EffectRow';
import { ArmyRow } from '../shell/ArmyRow';
import { AlertRow } from '../shell/AlertRow';
import { StatusBanner } from '../shell/StatusBanner';
import { RaceCrest } from '../shell/RaceCrest';
import { StandingCard, ResourcesCard } from '../shell/ResourceSummaryCards';
import { useThroneStatusBanners } from './useStatusBanners';
import { NotificationBellLink } from './NotificationBellLink';
import { IncomingActivitySection } from './IncomingActivitySection';
import {
  summarizeArmyUnits,
  ordinalSuffix,
  describeCombatEvent,
  stripEmbeddedLocation,
  formatCurrentGameDate,
} from './throneFormatting';

const RECENT_ACTIVITY_CAP_DESKTOP = 5;

interface DesktopThroneShellProps {
  province: Province;
}

/**
 * SF-122 Component 2: `useAppShellPlatform() === 'desktop'` rendering. No sidebar of its own —
 * `AppShellLayout` already renders the persistent `Sidebar` around it. Divergent chrome from
 * `MobileThroneShell` (Key Implementation Note 1) — a separate DOM structure, not the same tree
 * with Tailwind breakpoint classes toggling visibility. The two shells DO share the resolved
 * `<StatusBanner>` set and `deriveAlerts()` output (`useThroneStatusBanners`, `deriveAlerts`) —
 * that's shared *logic*, not shared chrome.
 */
export function DesktopThroneShell({ province }: DesktopThroneShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const { count: unreadCount } = useUnreadCount();
  const { banners, announcement } = useThroneStatusBanners(province);
  const alerts = deriveAlerts(province);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const { metadata, military, population, magic, sync } = province;

  const recentActivity = [...military.recentCombat]
    .sort((a, b) => b.launchTick - a.launchTick)
    .slice(0, RECENT_ACTIVITY_CAP_DESKTOP);

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        {metadata.name}
      </h1>

      {/* Identity header + inline StatusBanner stack, wrapping to its own row at the 1024px
          content-width boundary rather than compressing the identity header (Phase 2 C11). */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-shrink-0">
          <RaceCrest race={metadata.race} size={48} />
          {/* Province name lives in the <h1> above, not repeated here. */}
          <div>
            <div className="text-xs text-game-400">
              {getRaceDisplayName(metadata.race)} · {getPersonalityDisplayName(metadata.personality)} ·{' '}
              {stripEmbeddedLocation(metadata.location.kingdomName, metadata.location.kingdomId)} ({metadata.location.kingdom}:{metadata.location.island})
            </div>
            <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
              {formatCurrentGameDate(sync.currentTick)}
            </div>
          </div>
          <NotificationBellLink unreadCount={unreadCount} />
        </div>

        {banners.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-[280px]">
            {banners.map((banner) => (
              <div key={banner.tone} className="flex-1 min-w-[220px]">
                <StatusBanner {...banner} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div aria-live="polite" aria-atomic="false" className="sr-only">
        {announcement}
      </div>

      {alerts.length > 0 && (
        <GlanceCard title="Alerts" icon="⚠️" badge={alerts.length}>
          {alerts.map((alert, idx) => (
            <AlertRow
              key={idx}
              severity={alert.severity}
              message={alert.message}
              actionRoute={alert.actionRoute}
              actionLabel={alert.actionLabel}
            />
          ))}
        </GlanceCard>
      )}

      {/* 2-column grid: left Standing + Resources, right Power + Armies Away + Active Effects. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="flex flex-col gap-4">
          <StandingCard province={province} />
          <ResourcesCard province={province} />
        </div>

        <div className="flex flex-col gap-4">
          <GlanceCard title="Military" icon="⚔️">
            <MetricRow label="Offense" value={formatNumber(military.offensePoints)} unit="op" weight="primary" />
            <MetricRow label="Defense" value={formatNumber(military.defensePoints)} unit="dp" weight="primary" />
            <MetricRow label="Generals" value={formatNumber(population.generals)} unit="" weight="tertiary" />
            <MetricRow label="Soldiers" value={formatNumber(population.soldiers)} unit="" weight="tertiary" />
            <MetricRow label={getRaceUnitName(metadata.race, 'defense')} value={formatNumber(population.defenseSpecialists)} unit="" weight="tertiary" />
            <MetricRow label={getRaceUnitName(metadata.race, 'offense')} value={formatNumber(population.offenseSpecialists)} unit="" weight="tertiary" />
            <MetricRow label={getRaceUnitName(metadata.race, 'elite')} value={formatNumber(population.elites)} unit="" weight="tertiary" />
            <MetricRow label="Thieves" value={formatNumber(population.thieves)} unit="" weight="tertiary" />
            <MetricRow label="Wizards" value={formatNumber(population.wizards)} unit="" weight="tertiary" />
          </GlanceCard>

          {military.returningArmies.length > 0 && (
            <GlanceCard title="Armies Away" icon="🚩">
              {military.returningArmies.map((army) => (
                <ArmyRow
                  key={army.generalNumber}
                  label={`${army.generalNumber}${ordinalSuffix(army.generalNumber)} Army`}
                  ticksRemaining={army.ticksRemaining}
                  returnTimeFraction={army.returnTimeFraction}
                  tickIntervalMs={sync.tickIntervalMs}
                  unitsSummary={summarizeArmyUnits(army)}
                />
              ))}
            </GlanceCard>
          )}

          {magic.activeEffects.length > 0 && (
            <GlanceCard title="Active Effects" icon="✨">
              {magic.activeEffects.map((effect, idx) => (
                <EffectRow
                  key={idx}
                  name={formatEnumLabel(effect.effectType)}
                  ticksRemaining={effect.ticksRemaining}
                  indefinite={effect.indefinite}
                  urgent={effect.ticksRemaining <= 1 && !effect.indefinite}
                  beneficial={isEffectBeneficialToOwner(effect.effectType)}
                />
              ))}
            </GlanceCard>
          )}
        </div>
      </div>

      {/* Recent Activity — below the grid, not inside it, full-width. Always renders.
          SF-123: gains an "Incoming" subsection (hostile events targeting this province) above
          the existing, entirely-unchanged "Outgoing" subsection (today's military.recentCombat
          content) — Decision 10: the card is augmented, not replaced, so a peaceful/purely-
          offensive player still sees their own outgoing activity even with zero incoming. */}
      <GlanceCard
        title="Recent Activity"
        icon="📜"
      >
        <IncomingActivitySection limit={RECENT_ACTIVITY_CAP_DESKTOP} />

        <div>
          <h3
            className="text-xs font-semibold uppercase tracking-wide mb-1"
            style={{ color: COLORS.text.tertiary, letterSpacing: '0.06em' }}
          >
            Outgoing
          </h3>
          {recentActivity.length === 0 ? (
            <div className="text-sm text-game-400">No recent activity.</div>
          ) : (
            recentActivity.map((event, idx) => {
              const { color, message } = describeCombatEvent(event);
              return (
                <div key={idx} className="text-sm" style={{ color }}>
                  {message}
                </div>
              );
            })
          )}
        </div>
      </GlanceCard>
    </div>
  );
}
